/**
 * ArtifactPreview — render unificado del documento oficial completo.
 *
 * Componente reutilizable para varias vistas. Diseño con sistema de
 * `mode` + `slots` para que NO terminemos con 3 componentes que hacen
 * casi lo mismo (viewer, preview de draft, revisión de approver, diff
 * de versiones).
 *
 * **Modos hoy** (Sprint Preview-1):
 *   - `view`     → oficial aprobado o cualquier artifact en read-only
 *   - `preview`  → vista previa del draft mientras el autor edita
 *
 * **Modos futuros** (placeholders documentados — tocar slots, no UI):
 *   - TODO `review`  → revisor decidiendo aprobar/rechazar (B.6.c.2)
 *   - TODO `diff`    → comparar dos versiones lado a lado
 *
 * **Slots opcionales** (cada caller usa los que le hacen sentido):
 *   - `banner`         → aviso encima del header (ej. "Vista previa
 *                        del draft", o "Tienes 3 cambios por revisar")
 *   - `metadataExtras` → bloque adicional en el header (versión, hash,
 *                        firmantes, fecha aprobación)
 *   - `topToolbar`     → botones a la derecha del título (back link,
 *                        descargar, volver al editor)
 *   - `footerActions`  → barra sticky al fondo (decisión de approver,
 *                        TODO en B.6.c.2)
 *
 * **Slots futuros NO implementados todavía** (los listo aquí para que
 * cuando llegue el sprint correspondiente sepamos qué cablear):
 *
 *   - TODO `commentsBySectionId` (B.6.c.2): mapa section_id → comentarios.
 *     Cada sección se envuelve con un wrapper que muestra dot/contador
 *     de comentarios + sidebar/popover con el thread.
 *
 *   - TODO `decisionPanel` (B.6.c.2): slot footer sticky con botones
 *     "Aprobar" / "Solicitar cambios" / "Rechazar" + textarea de
 *     justificación. El service layer dispara la transición de estado.
 *
 *   - TODO `diffAgainst` (versioning visual): tipo `ArtifactVersion |
 *     null`. Si presente, cada sección se renderiza con marks de
 *     adición/eliminación contra esa versión. Algoritmo: diff sobre
 *     TipTap JSON node por node.
 *
 *   (Resuelto en OT-0027, por otra vía: el PDF NO se arma desde este
 *   componente. El render vive en el backend — un documento oficial no
 *   puede verse distinto según el navegador y la configuración de
 *   impresión de quien lo descarga. Aquí solo va el botón que dispara la
 *   descarga, `DownloadPdfButton`.)
 *
 *   - TODO `signaturePanel` (firma digital, post-MVP): slot al final
 *     del doc que muestra firmantes con timestamp + hash de la firma.
 */

import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import i18n from 'i18next'
import { useMemo } from 'react'

import type { Artifact, ArtifactVersion } from '../../../api/artifacts'
import { isSupportedLocale, toIntlLocale } from '../../../i18n/config'
import { BackButton } from '../../shell/BackButton'
import { SectionRenderer } from './SectionRenderer'
import type {
  TemplateDefinition,
  TemplateI18nSection,
  TemplateSectionMeta,
} from './section_types'

export type ArtifactPreviewMode = 'view' | 'preview'
// TODO(B.6.c.2): añadir 'review' y 'diff' al tipo cuando lleguen.

export interface ArtifactPreviewProps {
  artifact: Artifact
  version: ArtifactVersion
  templateDef: TemplateDefinition
  /** Locale de render del template (`'es'` | `'en'`). Lo resuelve el
   * caller desde `project.content_language`. */
  renderLocale: 'es' | 'en'
  mode: ArtifactPreviewMode
  /** "Volver" del header. Si null, no se muestra el botón. El `fallback`
   * se ejecuta SOLO cuando no hay historial dentro de la app (deep link,
   * recarga directa en la página de detalle); el caso normal usa
   * `history.back()` para preservar los search params del listado de
   * documentos oficiales (q/superseded). */
  backLink?: {
    label: string
    fallback: () => void
  } | null

  // ─── Slots ────────────────────────────────────────────────────────
  /** Banner soft encima del header (ej. "Vista previa del draft"). */
  banner?: React.ReactNode
  /** Extras debajo del título (típicamente metadata: versión, hash,
   * firmantes). Si se omite, no se renderiza nada extra. */
  metadataExtras?: React.ReactNode
  /** Botones/acciones a la derecha del header (descargar, volver al
   * editor, etc.). */
  topToolbar?: React.ReactNode
  /** Barra inferior sticky con acciones primarias (TODO B.6.c.2:
   * aprobar/rechazar). */
  footerActions?: React.ReactNode
  /** OT-0007 §4.6 — reemplaza el árbol de secciones cuando el artifact
   * es `source_mode='upload'`: no hay `content` estructurado que
   * mostrar, el documento ES el archivo cargado. El caller decide (via
   * `UploadedFileViewer`); el header/badge/metadata de arriba no
   * cambian. */
  bodySlot?: React.ReactNode
  /** Muestra el `section_id` de cada sección. Default `true`; el caller lo
   *  apaga para quien no lee los recursos internos del tenant (OT-0017). */
  showSectionIds?: boolean

  // ─── Slots NO implementados todavía (referencia para sprints futuros) ─
  // TODO(B.6.c.2) commentsBySectionId?: Map<string, Comment[]>
  // TODO(versioning) diffAgainst?: ArtifactVersion | null
  // TODO(signature) signaturePanel?: React.ReactNode
}

export function ArtifactPreview({
  artifact,
  version,
  templateDef,
  renderLocale,
  mode,
  backLink = null,
  banner,
  metadataExtras,
  topToolbar,
  footerActions,
  bodySlot,
  showSectionIds = true,
}: ArtifactPreviewProps) {
  const i18nBlock = templateDef.i18n?.[renderLocale]
  const i18nSectionsByCode = useMemo(() => {
    const m = new Map<string, TemplateI18nSection>()
    if (i18nBlock?.sections) {
      for (const s of i18nBlock.sections) m.set(s.section_id, s)
    }
    return m
  }, [i18nBlock])

  const orderedStructuralSections: TemplateSectionMeta[] = useMemo(() => {
    if (!templateDef.sections) return []
    return [...templateDef.sections].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    )
  }, [templateDef])

  return (
    <div className={mode === 'preview' ? 'max-w-5xl pb-24' : 'max-w-5xl'}>
      {backLink ? (
        <BackButton
          fallback={backLink.fallback}
          className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-4"
        >
          {backLink.label}
        </BackButton>
      ) : null}

      {banner ? <div className="mb-4">{banner}</div> : null}

      <header className="mb-6">
        {/* Toolbar en su propia fila, siempre right-aligned. No comparte
            fila con el bloque título/metadata para evitar wrap a la
            izquierda cuando el metadata crece (caso canon aprobado con
            hash + fecha + indexing badge). El orden visual deja
            "Comentarios" como último a la derecha — ancla estable para
            futuros botones (export PDF, descarga, etc.). */}
        {topToolbar ? (
          <div className="mb-4 flex items-center justify-end gap-2">
            {topToolbar}
          </div>
        ) : null}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {/* Doc 07 — el badge se decide a nivel VERSION, no a nivel
                artifact. Durante v(N+1) IN_REVIEW, artifact.is_canon
                sigue true (v(N) es canon) y artifact.current_version_id
                ya apunta a v(N+1) — pero v(N+1) NO es oficial todavía.
                isVersionCanon = la versión que veo es la activa Y el
                artifact tiene canon vigente Y esta versión ya fue
                aprobada. Sin las tres condiciones, no es oficial. */}
            <ArtifactStatusBadge
              artifactStatus={artifact.status}
              versionApprovedAt={version.approved_at}
              isVersionCanon={
                artifact.is_canon &&
                version.approved_at !== null &&
                version.id === artifact.current_version_id
              }
              mode={mode}
            />
            {artifact.code ? (
              <span className="text-[10px] font-mono text-ink-subtle">
                {artifact.code}
              </span>
            ) : null}
          </div>
          <h1 className="font-display text-2xl font-semibold text-ink">
            {artifact.name}
          </h1>
          {metadataExtras}
        </div>
      </header>

      <div className="space-y-4">
        {bodySlot ?? (
          <SectionTreePreview
            nodes={orderedStructuralSections}
            cursor={version.content as Record<string, unknown>}
            i18nMap={i18nSectionsByCode}
            depth={0}
            locale={renderLocale}
            showSectionIds={showSectionIds}
          />
        )}
      </div>

      {footerActions ? (
        // Sticky bottom — el offset left-60 matches sidebar width (igual
        // pattern que el editor de drafts). TODO(B.6.c.2): este es el
        // anchor del decisionPanel cuando llegue review flow.
        <div className="fixed bottom-0 left-60 right-0 bg-surface border-t border-line px-8 py-3 flex items-center justify-end gap-2 z-40">
          {footerActions}
        </div>
      ) : null}
    </div>
  )
}

// ─── Recursive section tree (con repeatables) ────────────────────────────
/**
 * Walk recursivo del árbol del template aplicando las MISMAS reglas que
 * `_walk_for_expansion` del backend (`section_topology.py`):
 *
 *   - Container con repeatable child → consume `cursor[section_id]` como
 *     array de instancias y renderiza cada una con sus children.
 *   - Container transparente (children sin rep) → recurse al mismo cursor.
 *   - Hoja → delega a `<SectionRenderer>` con el value resuelto.
 *
 * Sin esto el preview mostraba el container `casos_de_uso` como
 * "Structural container — groups its child sections" sin expandir las
 * instancias del array.
 */
function SectionTreePreview({
  nodes,
  cursor,
  i18nMap,
  depth,
  locale,
  showSectionIds,
}: {
  nodes: TemplateSectionMeta[]
  cursor: Record<string, unknown> | unknown
  i18nMap: Map<string, TemplateI18nSection>
  depth: number
  locale: 'es' | 'en'
  showSectionIds: boolean
}) {
  const ordered = [...nodes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  return (
    <>
      {ordered.map((meta) => (
        <SectionTreePreviewNode
          key={meta.section_id}
          meta={meta}
          cursor={cursor}
          i18nMap={i18nMap}
          depth={depth}
          locale={locale}
          showSectionIds={showSectionIds}
        />
      ))}
    </>
  )
}

function SectionTreePreviewNode({
  meta,
  cursor,
  i18nMap,
  depth,
  locale,
  showSectionIds,
}: {
  meta: TemplateSectionMeta
  cursor: unknown
  i18nMap: Map<string, TemplateI18nSection>
  depth: number
  locale: 'es' | 'en'
  showSectionIds: boolean
}) {
  const { t } = useTranslation('projects')
  const i18nSection = i18nMap.get(meta.section_id)
  const title = i18nSection?.title ?? meta.section_id
  const children =
    (meta['children'] as TemplateSectionMeta[] | undefined) ?? []
  const hasChildren = children.length > 0
  const repChild = hasChildren
    ? children.find(
        (c) => (c as { repeatable?: boolean }).repeatable === true,
      )
    : undefined

  // Caso 1: container con repeatable child → expandir instancias.
  if (hasChildren && repChild) {
    const instances =
      cursor && typeof cursor === 'object' && !Array.isArray(cursor)
        ? (cursor as Record<string, unknown>)[meta.section_id]
        : undefined
    const repTitle =
      i18nMap.get(repChild.section_id)?.title ?? repChild.section_id
    const repChildren =
      (repChild['children'] as TemplateSectionMeta[] | undefined) ?? []
    const arr = Array.isArray(instances) ? instances : []

    return (
      <article className="bg-surface border border-line rounded-md p-5">
        <header className="mb-3 flex items-baseline gap-3 flex-wrap">
          <h3 className="font-display text-lg font-semibold text-ink">
            {title}
          </h3>
          <span className="text-xs text-ink-subtle">
            {arr.length} {repTitle}
          </span>
          {showSectionIds ? (
            <span className="ml-auto font-mono text-[10px] text-ink-subtle">
              {meta.section_id}
            </span>
          ) : null}
        </header>
        {arr.length === 0 ? (
          <p className="font-body text-sm text-ink-subtle italic">
            {t('official_viewer.repeatable_empty_in_preview')}
          </p>
        ) : (
          <div className="space-y-4">
            {arr.map((instance, idx) => (
              <div
                key={idx}
                className="rounded-md border border-line/60 bg-canvas/50 p-4"
              >
                <h4 className="font-display text-base font-medium text-ink mb-3 pb-2 border-b border-line/60">
                  {t('official_viewer.repeatable_instance_label', {
                    singular: repTitle,
                    n: idx + 1,
                  })}
                </h4>
                <div className="space-y-3">
                  <SectionTreePreview
                    nodes={repChildren}
                    cursor={instance}
                    i18nMap={i18nMap}
                    depth={depth + 1}
                    locale={locale}
                    showSectionIds={showSectionIds}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </article>
    )
  }

  // Caso 2: container transparente (children sin rep) → recurse mismo cursor.
  if (hasChildren) {
    return (
      <article className="bg-surface border border-line rounded-md p-5">
        <header className="mb-3 flex items-baseline gap-3 flex-wrap">
          <h3 className="font-display text-lg font-semibold text-ink">
            {title}
          </h3>
          {showSectionIds ? (
            <span className="ml-auto font-mono text-[10px] text-ink-subtle">
              {meta.section_id}
            </span>
          ) : null}
        </header>
        <div className="space-y-3">
          <SectionTreePreview
            nodes={children}
            cursor={cursor}
            i18nMap={i18nMap}
            depth={depth + 1}
            locale={locale}
            showSectionIds={showSectionIds}
          />
        </div>
      </article>
    )
  }

  // Caso 3: hoja → delegate al SectionRenderer existente.
  const value =
    cursor && typeof cursor === 'object' && !Array.isArray(cursor)
      ? (cursor as Record<string, unknown>)[meta.section_id]
      : undefined
  const columnLabels =
    i18nSection &&
    typeof i18nSection['column_labels'] === 'object' &&
    i18nSection['column_labels'] !== null
      ? (i18nSection['column_labels'] as Record<string, string>)
      : undefined
  return (
    <SectionRenderer
      meta={meta}
      title={title}
      value={value}
      {...(columnLabels ? { columnLabels } : {})}
      locale={locale}
      showSectionId={showSectionIds}
    />
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────
/**
 * Doc 07 — el badge refleja el estado de la VERSIÓN que se está viendo,
 * no el del artifact global. Cuando un artifact tiene v(N) canon + v(N+1)
 * en revisión, `artifact.is_canon` es true pero la versión visible
 * (v(N+1)) NO es oficial todavía. Reglas:
 *
 *   1. mode='preview'           → PREVIEW
 *   2. version no aprobada      → DRAFT / IN_REVIEW (según artifact.status)
 *   3. version aprobada y canon → OFFICIAL
 *   4. version aprobada no canon → SUPERSEDED
 */
function ArtifactStatusBadge({
  artifactStatus,
  versionApprovedAt,
  isVersionCanon,
  mode,
}: {
  artifactStatus: string
  versionApprovedAt: string | null
  isVersionCanon: boolean
  mode: ArtifactPreviewMode
}) {
  const { t } = useTranslation('projects')

  if (mode === 'preview') {
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-review-soft text-review border border-review/40">
        {t('official_viewer.status_badge.preview')}
      </span>
    )
  }

  let label: string
  let className: string
  let showIcon = false

  if (versionApprovedAt === null) {
    // Version no aprobada: depende del status del artifact (la versión
    // bajo review hereda IN_REVIEW; el resto es draft activa).
    if (artifactStatus === 'in_review') {
      label = t('official_viewer.status_badge.in_review')
      className = 'bg-review-soft text-review'
    } else {
      label = t('official_viewer.status_badge.draft')
      className = 'bg-surface-muted text-ink-muted'
    }
  } else if (isVersionCanon) {
    label = t('official_viewer.status_badge.official')
    className = 'bg-official-soft text-official'
    showIcon = true
  } else {
    label = t('official_viewer.status_badge.superseded')
    className = 'bg-surface-muted text-ink-subtle line-through'
  }

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider flex items-center gap-1 ${className}`}
    >
      {showIcon ? <ShieldCheck className="w-3 h-3" /> : null}
      {label}
    </span>
  )
}

// ─── Metadata helpers públicos ────────────────────────────────────────────
// El caller arma su propio bloque de metadata en `metadataExtras`
// reutilizando estos helpers. Los exportamos porque sirven en
// distintas vistas (viewer, preview, futuro review/diff).

export function ArtifactMetadata({
  artifact,
  versionId,
  versionNumber,
  versionHash,
  versionApprovedAt,
  versionIndexedAt,
  contentLanguage,
  subphaseCode,
  templateKey,
  templateVersion,
}: {
  artifact: Artifact
  /** Doc 07 — id de la version que se está mostrando. Se usa para
   *  comparar contra `current_version_id` y decidir si esta versión es
   *  el canon vigente (vs. una v(N+1) en review o una vieja superseded). */
  versionId: string
  versionNumber: number
  versionHash: string | null
  versionApprovedAt: string | null
  /** Doc 22 Fase 8.j — ISO timestamp del canon indexing (Qdrant +
   *  Neo4j). Null mientras la task corre o si falló permanentemente.
   *  Cuando aprobado pero sin indexar, mostramos badge "indexing
   *  pending". */
  versionIndexedAt?: string | null
  contentLanguage: string
  /** Identificadores internos (código de sub-fase, `template_key`), sin
   *  etiqueta legible. El caller los omite para quien no lee los recursos
   *  internos del tenant (OT-0017); sin ellos, su fila no se pinta. */
  subphaseCode?: string | undefined
  templateKey?: string | undefined
  templateVersion?: string | undefined
}) {
  const { t } = useTranslation('projects')
  // Doc 07 — el label sigue las mismas reglas que el badge: la
  // versión es oficial sólo si está aprobada Y es la canon activa del
  // artifact. Una v(N+1) en review tiene approved_at=null aunque
  // artifact.is_canon=true (v(N) sigue siendo canon hasta que v(N+1)
  // se apruebe).
  const isVersionCanon =
    artifact.is_canon &&
    versionApprovedAt !== null &&
    versionId === artifact.current_version_id
  const versionLabel = isVersionCanon
    ? t('official_viewer.version_official', { n: versionNumber })
    : versionApprovedAt === null
      ? t('official_viewer.version_draft')
      : t('official_viewer.version_superseded', { n: versionNumber })

  // Doc 22 Fase 8.j — pipeline de canon indexing en curso o pendiente.
  // El backend `_mark_canon` encoló la task async; mientras `indexed_at`
  // sea null y la version esté aprobada, los vectores y el grafo
  // todavía no están listos. RAG retrieval cae a versions previas.
  const isIndexingPending =
    versionApprovedAt !== null &&
    versionIndexedAt !== undefined &&
    versionIndexedAt === null

  return (
    <>
      {isIndexingPending ? (
        <div
          role="status"
          className="mt-3 inline-flex items-center gap-2 rounded-md border border-review/40 bg-review-soft/40 px-2.5 py-1 text-[11px] text-review"
          title={t('official_viewer.indexing_pending.tooltip')}
        >
          <span
            className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-review"
            aria-hidden="true"
          />
          <span>{t('official_viewer.indexing_pending.label')}</span>
        </div>
      ) : null}
      <dl className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
        <MetaRow label={t('official_viewer.metadata.version')} value={versionLabel} />
        {versionHash ? (
          <MetaRow
            label={t('official_viewer.metadata.hash')}
            value={truncateHash(versionHash)}
            mono
          />
        ) : null}
        {versionApprovedAt ? (
          <MetaRow
            label={t('official_viewer.metadata.approved_at')}
            value={formatDate(versionApprovedAt)}
          />
        ) : null}
        {versionIndexedAt ? (
          <MetaRow
            label={t('official_viewer.metadata.indexed_at')}
            value={formatDate(versionIndexedAt)}
          />
        ) : null}
        <MetaRow
          label={t('official_viewer.metadata.language')}
          value={contentLanguage}
          mono
        />
        {subphaseCode ? (
          <MetaRow
            label={t('official_viewer.metadata.subphase')}
            value={subphaseCode}
            mono
          />
        ) : null}
        {templateKey ? (
          <MetaRow
            label={t('official_viewer.metadata.template')}
            value={templateVersion ? `${templateKey} · v${templateVersion}` : templateKey}
            mono
          />
        ) : null}
        {/* TODO(signature): cuando exista firma digital, agregar MetaRow
            con lista de firmantes + timestamp + hash de firma. */}
      </dl>
    </>
  )
}

function MetaRow({
  label,
  value,
  mono = false,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-ink-subtle font-medium">
        {label}
      </dt>
      <dd
        className={
          mono
            ? 'text-ink font-mono text-xs mt-0.5'
            : 'text-ink text-xs mt-0.5'
        }
      >
        {value}
      </dd>
    </div>
  )
}

// ─── Helpers de formato (compartidos) ─────────────────────────────────────
function truncateHash(hash: string): string {
  if (hash.length <= 13) return hash
  return `${hash.slice(0, 6)}…${hash.slice(-6)}`
}

function formatDate(iso: string): string {
  // UI-locale (i18n.language) elevado a región vía `toIntlLocale` —
  // evita el formato es-ES default cuando el user navega en español.
  const locale = toIntlLocale(
    isSupportedLocale(i18n.language) ? i18n.language : 'es',
  )
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
